import * as React from 'react';

import {PlanComparisonTableWrapper} from 'shells/billing/plan-comparison/plan-comparison-table-wrapper';
import {PlanPicker} from 'shells/billing/plans/plan-picker';
import type {Cycle} from 'shells/billing/types';

import type {GetAvailablePlans_recurlyAvailablePlans_crm_plans as Plan} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../../../analytics-client';

import {useGetPlanComparisons} from '../../graphql/hooks';

import {getPlanCodeForGivenCycle} from '../helpers';
import {SuiteHeader} from '../suite-header/suite-header';

type Props = {
    plans: Plan[];
    errorMessage: string | null | undefined;
    isLoadingPlans: boolean;
    selectedPlanCode: string | null | undefined;
    numUsers: string | null | undefined;
    numEnabledUsers?: number;
    numMarketingLicenses?: number;
    cycle: Cycle;
    setSelectedCrmPlanCode: (planCode: string) => void;
    setNumUsers: (numUsers: string) => void;
    setCycle: (cycle: Cycle) => void;
    isTrial: boolean;
    scrollContainerId: string;
};

export function SalesSuitePicker(props: Props) {
    const cyclePlans = props.plans.filter((plan) => plan.cycleText === props.cycle);
    const isTrial = props.isTrial;
    const {planComparisons} = useGetPlanComparisons('sales');

    const handleCycleChange = (cycle: Cycle) => {
        // When a new cycle is selected, we need to keep the plan selection,
        // so we update the plan code to match the new cycle.
        if (cycle !== props.cycle && props.selectedPlanCode) {
            const updatedPlanCode = getPlanCodeForGivenCycle(props.selectedPlanCode, cycle);
            props.setSelectedCrmPlanCode(updatedPlanCode);
        }

        props.setCycle(cycle);
    };

    React.useEffect(() => {
        if (isTrial) {
            analyticsClient.track(
                analyticsClient.EVENTS.TRACK_CUSTOMER_TRIAL_VISITS_BILLING_PLAN_PICKER
            );
        }
    }, [isTrial]);

    return (
        <div className='pad-16 flex flex-dir-col full-width gap-8 border-box'>
            <SuiteHeader
                selectedCycle={props.cycle}
                numUsers={props.numUsers || ''}
                numEnabledUsers={props.numEnabledUsers}
                numMarketingLicenses={props.numMarketingLicenses}
                onCycleChange={handleCycleChange}
                onNumUsersChange={props.setNumUsers}
                isTrial={isTrial}
                suite='sales'
            />
            <PlanPicker
                plans={cyclePlans}
                selectedPlanCode={props.selectedPlanCode}
                onSelectedPlanCodeChange={props.setSelectedCrmPlanCode}
                isLoading={props.isLoadingPlans}
                errorMessage={props.errorMessage}
            />
            {!props.isLoadingPlans && planComparisons && (
                <PlanComparisonTableWrapper
                    planComparisons={planComparisons}
                    scrollContainerId={props.scrollContainerId}
                />
            )}
        </div>
    );
}
